import React, { useState, useEffect } from 'react';
import { Sparkles, ThumbsUp, ThumbsDown, Copy, Check } from 'lucide-react';
import { sendFeedback } from '@/services/chatApi';
import { FeedbackProps } from '@/types/chat';

const FEEDBACK_REASONS = [
  { value: 'unclear', label: 'Unclear answer' },
  { value: 'inaccurate', label: 'Wrong/inaccurate answer' },
  { value: 'formatting', label: 'Dislike/Bad answer formatting' },
  { value: 'other', label: 'Other' },
];

export const Feedback = ({
  conversation_id,
  request_id,
  existingFeedback,
  messageContent,
}: FeedbackProps) => {
  const [feedbackStatus, setFeedbackStatus] = useState<
    'positive' | 'negative' | null
  >(null);
  const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [positiveSubmitted, setPositiveSubmitted] = useState(false);
  const [showNegativeForm, setShowNegativeForm] = useState(false);
  const [responseMessage, setResponseMessage] = useState('');
  const [isError, setIsError] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (existingFeedback?.feedback_type) {
      const normalizedType = existingFeedback.feedback_type
        .toLowerCase()
        .trim() as 'positive' | 'negative';
      if (normalizedType === 'positive' || normalizedType === 'negative') {
        setFeedbackStatus(normalizedType);
        setPositiveSubmitted(normalizedType === 'positive');
      }
    } else {
      setFeedbackStatus(null);
      setPositiveSubmitted(false);
    }
  }, [existingFeedback]);

  const handleCopy = async () => {
    if (!messageContent) return;
    try {
      await navigator.clipboard.writeText(messageContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard API may be blocked */
    }
  };

  const handleFeedback = async (status: 'positive' | 'negative') => {
    if (!request_id || !conversation_id) {
      console.error(
        'Cannot submit feedback: request_id or conversation_id is missing',
      );
      setResponseMessage(
        'Unable to submit feedback: missing required information',
      );
      setIsError(true);
      return;
    }

    try {
      const feedbackData = {
        conversation_id,
        request_id,
        feedback_type: status,
        ...(status === 'negative' &&
          selectedReasons.length > 0 && { reason: selectedReasons.join(', ') }),
        ...(status === 'negative' &&
          feedbackMessage.trim() && { comment: feedbackMessage }),
      };

      const response = await sendFeedback(feedbackData);

      const isErrorResponse =
        response.includes('Failed') ||
        response.includes('error') ||
        response.includes('Unable');
      setIsError(isErrorResponse);
      setResponseMessage(response);

      if (!isErrorResponse) {
        if (status === 'positive') {
          setPositiveSubmitted(true);
        } else {
          setShowNegativeForm(false);
          setPositiveSubmitted(false);
        }
        setSelectedReasons([]);
        setFeedbackMessage('');
      }
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      setResponseMessage('An unexpected error occurred. Please try again.');
      setIsError(true);
      setFeedbackStatus(null);
    }
  };

  return (
    <div className="w-full mt-3 pt-3 border-t border-slate-100">
      <div className="flex items-center gap-1.5">
        {/* Copy button */}
        <button
          type="button"
          onClick={handleCopy}
          className="p-1.5 text-[#A0AEB4] hover:text-[#252F33] transition-colors rounded"
          aria-label="Copy response">
          {copied ? (
            <Check className="h-4 w-4 text-green-500" />
          ) : (
            <Copy className="h-4 w-4" />
          )}
        </button>

        {/* Thumbs up */}
        <button
          type="button"
          onClick={() => {
            setFeedbackStatus('positive');
            setResponseMessage('');
            setIsError(false);
            setShowNegativeForm(false);
            handleFeedback('positive');
          }}
          disabled={positiveSubmitted}
          className={`p-1.5 rounded transition-colors ${
            feedbackStatus === 'positive'
              ? 'text-green-500'
              : 'text-[#A0AEB4] hover:text-[#252F33]'
          } ${positiveSubmitted ? 'cursor-default' : ''}`}
          aria-label="Good response">
          <ThumbsUp className="h-4 w-4" />
        </button>

        {/* Thumbs down */}
        <button
          type="button"
          onClick={() => {
            setFeedbackStatus('negative');
            setShowNegativeForm(true);
            setResponseMessage('');
            setIsError(false);
          }}
          className={`p-1.5 rounded transition-colors ${
            feedbackStatus === 'negative'
              ? 'text-red-400'
              : 'text-[#A0AEB4] hover:text-[#252F33]'
          }`}
          aria-label="Bad response">
          <ThumbsDown className="h-4 w-4" />
        </button>
      </div>

      {feedbackStatus === 'negative' && showNegativeForm && (
        <form className="mt-3 space-y-3">
          <fieldset>
            <legend className="block text-sm font-medium text-gray-700 mb-2">
              Reason(s) <span className="text-red-500">*</span>
            </legend>
            <div className="space-y-2">
              {FEEDBACK_REASONS.map(reason => (
                <label
                  key={reason.value}
                  className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                  <input
                    type="checkbox"
                    value={reason.value}
                    checked={selectedReasons.includes(reason.value)}
                    onChange={e => {
                      if (e.target.checked) {
                        setSelectedReasons([...selectedReasons, reason.value]);
                      } else {
                        setSelectedReasons(
                          selectedReasons.filter(r => r !== reason.value),
                        );
                      }
                    }}
                    className="h-4 w-4 rounded border-gray-300 text-[#007CA0] focus:ring-[#007CA0]"
                  />
                  <span className="text-sm text-gray-700">{reason.label}</span>
                </label>
              ))}
            </div>
          </fieldset>
          <div>
            <label
              htmlFor="feedback-comment"
              className="block text-sm font-medium text-gray-700 mb-1">
              Additional comments <span className="text-red-500">*</span>
            </label>
            <textarea
              id="feedback-comment"
              className="w-full border border-gray-300 rounded-md p-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
              value={feedbackMessage}
              onChange={e => setFeedbackMessage(e.target.value)}
              placeholder="What could be improved?"
              required
            />
          </div>
          <button
            disabled={selectedReasons.length === 0 || !feedbackMessage.trim()}
            type="button"
            onClick={() => {
              handleFeedback('negative');
            }}
            className="px-4 py-2 rounded-md bg-[#007CA0] text-white transition hover:bg-[#00688A] disabled:opacity-50 disabled:cursor-not-allowed">
            Submit Feedback
          </button>
        </form>
      )}

      {responseMessage && (
        <div
          className={`mt-2 inline-flex max-w-md items-start gap-2 rounded-lg border px-3 py-2 text-sm shadow-sm transition-colors ${
            isError
              ? 'border-red-200 bg-red-50 text-red-700'
              : 'border-slate-200 bg-slate-50 text-slate-700'
          }`}>
          <Sparkles
            className={`h-4 w-4 mt-0.5 ${
              isError ? 'text-red-500' : 'text-slate-500'
            }`}
          />
          <span>{responseMessage}</span>
        </div>
      )}
    </div>
  );
};
